import { atom } from '@reatom/framework'
import { useAtom, useCtx } from '@reatom/npm-react'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useMemoOne } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage } from '@root/shared/ui-kit'

import { CanvasPnlBuilder } from '../lib/canvas-pnl-builder'

import type { BestDeal } from '@root/entities/profile'
import type { UserShort } from '@root/entities/user-short'
import type { Error } from '@root/shared'

type Props = {
  bestDeal: BestDeal.BestDeal
  rendererRef?: React.MutableRefObject<CanvasPnlBuilder | undefined>
  user: UserShort.UserShort | undefined
}

const noopStateAtom = atom(
  AsyncStateN.notInitiated<Error.UnknownError, undefined>(),
  'noopStateAtom',
)

export const PnlCardCanvas: React.FC<Props> = ({
  bestDeal,
  rendererRef,
  user,
}) => {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const ctx = useCtx()

  const pnlRenderer = useMemoOne(() => {
    if (!user) {
      return undefined
    }

    const renderer = new CanvasPnlBuilder(
      bestDeal,
      user,
      {
        'common.position': t('common.position'),
        'common.total-profit': t('common.total-profit'),
        'common.entry-price': t('common.entry-price'),
        'common.closed-price': t('common.closed-price'),
        'common.total-invested': t('common.total-invested'),
        'common.total-sold': t('common.total-sold'),
        'common.pnl': t('common.pnl'),
      },
      ctx,
    )

    return renderer
  }, [])

  const [state] = useAtom(pnlRenderer?.state ?? noopStateAtom)

  if (rendererRef) {
    rendererRef.current = pnlRenderer
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !pnlRenderer) {
      return
    }

    pnlRenderer.setCanvas(canvas).build()
  }, [pnlRenderer])

  if (!pnlRenderer) {
    return
  }

  const renderOverlay = () => {
    if (AsyncStateN.isLoading(state)) {
      return (
        <InfoContent>
          <CircularLoader />
        </InfoContent>
      )
    }

    if (AsyncStateN.isError(state)) {
      return (
        <InfoContent>
          <InfoMessage intent="error" />
        </InfoContent>
      )
    }

    return undefined
  }

  return (
    <Root>
      <Canvas ref={canvasRef} />
      {renderOverlay()}
    </Root>
  )
}

const Root = styled.div`
  width: 100%;
  position: relative;
`

const Canvas = styled.canvas`
  width: 100%;
`

const InfoContent = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  place-content: center;
`
